/**
 * Leaflet raster basemap resolution for Energy Asset OS maps.
 *
 * Prefer MapTiler when VITE_MAPTILER_KEY is set (same key pattern as prospectivity-map).
 * Otherwise fall back to Esri World Topo — no API key and no CARTO CDN watermark.
 *
 * Prod: set repo secret VITE_MAPTILER_KEY so GHA deploy bakes the keyed tiles into the build.
 * OSM direct tiles are avoided in the default fallback (rate-limit risk in prod).
 *
 * MapTiler Leaflet notes:
 * - Raster XYZ without `/256/` defaults to 512px tiles and needs tileSize:512 + zoomOffset:-1.
 * - Prefer the explicit `/256/` XYZ path so Leaflet’s default 256px grid aligns with markers.
 */

const MAPTILER_ATTRIBUTION =
  '&copy; <a href="https://www.maptiler.com/copyright/">MapTiler</a> · &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';

const ESRI_ATTRIBUTION =
  'Tiles &copy; <a href="https://www.esri.com/">Esri</a> — Source: Esri, USGS, NOAA';

/** Optional OSM fallback (documented for local/dev only; not the default). */
export const OSM_FALLBACK = {
  url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
  attribution:
    '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
  provider: 'osm',
  note: 'OSM public tiles are rate-limited — prefer MapTiler or Esri for production.',
};

/**
 * @param {{ VITE_MAPTILER_KEY?: string } | undefined} env
 * @returns {{
 *   url: string,
 *   attribution: string,
 *   provider: 'maptiler' | 'esri',
 *   tileSize?: number,
 *   zoomOffset?: number,
 * }}
 */
export function resolveBasemap(env = typeof import.meta !== 'undefined' ? import.meta.env : {}) {
  const key = String(env?.VITE_MAPTILER_KEY || '').trim();
  if (key) {
    return {
      // Explicit 256px XYZ — matches Leaflet defaults (no zoomOffset).
      url: `https://api.maptiler.com/maps/positron/256/{z}/{x}/{y}.png?key=${encodeURIComponent(key)}`,
      attribution: MAPTILER_ATTRIBUTION,
      provider: 'maptiler',
      tileSize: 256,
    };
  }
  return {
    // Esri World Topo — free for reasonable non-commercial/attribution use; no watermark.
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}',
    attribution: ESRI_ATTRIBUTION,
    provider: 'esri',
  };
}

/**
 * Props for react-leaflet `<TileLayer />`.
 *
 * Leaflet's `setOptions` copies `undefined` over defaults — so
 * `tileSize={undefined}` / `zoomOffset={undefined}` become NaN tile math and
 * throw "Attempted to load an infinite number of tiles". Only pass defined,
 * finite optional grid props.
 *
 * @param {{ url: string, attribution: string, tileSize?: number, zoomOffset?: number }} basemap
 * @returns {{ url: string, attribution: string, tileSize?: number, zoomOffset?: number }}
 */
export function leafletTileLayerProps(basemap) {
  const props = {
    url: basemap.url,
    attribution: basemap.attribution,
  };
  if (Number.isFinite(basemap.tileSize)) props.tileSize = basemap.tileSize;
  if (Number.isFinite(basemap.zoomOffset)) props.zoomOffset = basemap.zoomOffset;
  return props;
}
